const SELECTORS = {
  mainContent: '#MainContent',
  switcher: '[data-collection-switcher]',
  link: '[data-collection-switch-link]',
};

const SECTION_ID_PREFIX = 'shopify-section-';

const CACHE_MAX = 8;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in ms
const PREFETCH_LIMIT = 3;
const META_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in ms

const sectionCache = new Map();
const metaCache = new Map();
const instances = new WeakMap();
const instanceList = new Set();

const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));

function normalizeSectionId(id) {
  return id.startsWith(SECTION_ID_PREFIX) ? id.slice(SECTION_ID_PREFIX.length) : id;
}

/** Use native View Transitions if available, otherwise run synchronously. */
function startViewTransition(fn) {
  if (document.startViewTransition) {
    return document.startViewTransition(fn).finished;
  }
  fn();
  return Promise.resolve();
}

function canPrefetch() {
  const conn = navigator.connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  if (conn.effectiveType === '2g') return false;
  return true;
}

function createCacheKey(url) {
  const u = new URL(url.toString());
  u.hash = '';
  const allowed = ['sort_by', 'page'];
  for (const key of [...u.searchParams.keys()]) {
    if (!allowed.includes(key) && !key.startsWith('filter.')) {
      u.searchParams.delete(key);
    }
  }
  u.searchParams.sort();
  return u.toString();
}

function buildSectionURL(url, ids) {
  const u = new URL(url);
  u.searchParams.set('sections', ids.join(','));
  return u.toString();
}

function evictCache() {
  while (sectionCache.size > CACHE_MAX) {
    sectionCache.delete(sectionCache.keys().next().value);
  }
}

function evictMetaCache() {
  while (metaCache.size > CACHE_MAX) {
    metaCache.delete(metaCache.keys().next().value);
  }
}

function parseDocumentMeta(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const title = doc.querySelector('title')?.textContent?.trim() || '';
  const description = doc.querySelector('meta[name=\"description\"]')?.getAttribute('content')?.trim() || '';
  const canonical = doc.querySelector('link[rel=\"canonical\"]')?.getAttribute('href')?.trim() || '';
  return { title, description, canonical };
}

function applyDocumentMeta(meta) {
  if (meta.title) document.title = meta.title;

  const descEl = document.querySelector('meta[name=\"description\"]');
  if (meta.description) {
    if (descEl) descEl.setAttribute('content', meta.description);
    else {
      const el = document.createElement('meta');
      el.setAttribute('name', 'description');
      el.setAttribute('content', meta.description);
      document.head.appendChild(el);
    }
  } else if (descEl) {
    descEl.remove();
  }

  if (meta.canonical) {
    const canonicalEl = document.querySelector('link[rel=\"canonical\"]');
    if (canonicalEl) canonicalEl.setAttribute('href', meta.canonical);
  }
}

/**
 * Cancellable delay. Cleans up the abort listener whether the timer
 * or the abort fires first, preventing a dangling event listener.
 */
function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

function getCollectionHandle(url) {
  const segments = url.pathname.split('/').filter(Boolean);
  const index = segments.indexOf('collections');
  return index === -1 ? '' : segments[index + 1] || '';
}


class CollectionSwitcher {
  constructor(root) {
    this.root = root;
    this.requestId = 0;

    this.prefetchQueue = new Map();
    this.activePrefetches = 0;

    this.abortController = null;
    this.prefetchAbortController = new AbortController();

    this.observer = null;

    this.sectionIdsCache = null;

    /** The shopify-section-* wrapper that contains this switcher. Cached once. */
    this._ownSection = null;
  }

  get mainContent() {
    return document.querySelector(SELECTORS.mainContent);
  }

  /** The shopify-section-* element that wraps this switcher (cached). */
  get ownSection() {
    if (!this._ownSection) {
      this._ownSection = this.root.closest(`[id^="${SECTION_ID_PREFIX}"]`);
    }
    return this._ownSection;
  }

  init() {
    if (!(this.mainContent instanceof HTMLElement)) return;
    if (window.Shopify?.designMode) return;

    this.root.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);

    this.setupObserver();
    this.observeLinks();

    this.syncCurrentState(new URL(location.href));
    this.scrollActiveIntoView();
    this.scheduleInitialPrefetch();
  }

  destroy() {
    this.root.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
    this.abortController?.abort();
    this.prefetchAbortController?.abort();
    this.observer?.disconnect();
  }

  invalidateSectionCache() {
    this.sectionIdsCache = null;
    this._ownSection = null;
  }

  // ── Loading state ──────────────────────────────────────────────────────────

  setLoading(busy) {
    this.root.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  // ── Scroll helpers ─────────────────────────────────────────────────────────

  scrollActiveIntoView() {
    const active = this.root.querySelector('.collection-icon-scroller__link--current');
    active?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }

  // ── Event handlers ─────────────────────────────────────────────────────────

  onClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest(SELECTORS.link);
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;

    const url = this.getURL(link);
    if (!url) return;

    event.preventDefault();
    this.syncCurrentState(url);
    this.navigate(url, true);
  };

  onPopState = () => {
    const url = new URL(location.href);
    this.syncCurrentState(url);
    this.navigate(url, false);
  };

  // ── Navigation ─────────────────────────────────────────────────────────────

  async navigate(url, push) {
    const requestId = ++this.requestId;

    // Cancel any in-flight navigation and pending prefetches.
    this.abortController?.abort();
    this.prefetchAbortController?.abort();
    this.prefetchAbortController = new AbortController();
    this.abortController = new AbortController();

    this.setLoading(true);

    try {
      const sections = await this.getSections(url, this.abortController.signal);

      if (requestId !== this.requestId) return; // superseded

      await startViewTransition(() => {
        this.applySections(sections);
      });

      if (push) history.pushState({}, '', url.toString());

      // Re-observe after new link elements are in the DOM.
      this.observeLinks();

      // Keep browser tab title/description in sync with the new collection.
      this.updateDocumentMeta(url, this.abortController.signal, requestId);
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Hard fallback: full page load.
      location.href = url.toString();
    } finally {
      this.setLoading(false);
    }
  }

  // ── DOM update ─────────────────────────────────────────────────────────────

  /**
   * Swap only the INNER content of each section element.
   * Keeping the outer shopify-section-* wrapper means its id/attributes are
   * preserved, and custom elements inside will re-connect (connectedCallback)
   * because new DOM nodes are inserted — matching how Dawn's own facets.js works.
   */
  applySections(sections) {
    const template = document.createElement('template');

    for (const id in sections) {
      const current = document.getElementById(`${SECTION_ID_PREFIX}${id}`);
      if (!current) continue;

      template.innerHTML = sections[id];
      const next = template.content.firstElementChild;
      if (!next) continue;

      current.innerHTML = next.innerHTML;
    }
  }

  getSectionIds() {
    if (this.sectionIdsCache) return this.sectionIdsCache;

    const mainContent = this.mainContent;
    if (!mainContent) return [];

    const own = this.ownSection;
    const ids = [];

    for (const child of mainContent.children) {
      if (!(child instanceof HTMLElement)) continue;
      if (child === own) continue; // never swap the switcher itself

      const id = normalizeSectionId(child.id);
      if (id) ids.push(id);
    }

    this.sectionIdsCache = ids;
    return ids;
  }

  // ── Fetch / cache ──────────────────────────────────────────────────────────

  async getSections(url, signal) {
    const key = createCacheKey(url);
    const cached = sectionCache.get(key);

    if (cached && Date.now() - cached.time < CACHE_TTL) {
      // LRU: move to end
      sectionCache.delete(key);
      sectionCache.set(key, cached);
      return cached.data;
    }

    const sections = await this.fetchSections(url, signal);
    sectionCache.set(key, { data: sections, time: Date.now() });
    evictCache();
    return sections;
  }

  async fetchSections(url, signal) {
    const ids = this.getSectionIds();
    if (!ids.length) throw new Error('No downstream sections found');

    const endpoint = buildSectionURL(url, ids);
    const options = { credentials: 'same-origin', signal };

    let response = await fetch(endpoint, options);

    if (!response.ok) {
      // One retry after a short back-off (respects abort signal).
      await delay(200, signal);
      response = await fetch(endpoint, options);
      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
    }

    return response.json();
  }

  async updateDocumentMeta(url, signal, requestId) {
    try {
      const key = createCacheKey(url);
      const cached = metaCache.get(key);
      if (cached && Date.now() - cached.time < META_CACHE_TTL) {
        if (requestId !== this.requestId) return;
        applyDocumentMeta(cached.data);
        return;
      }

      const res = await fetch(url.toString(), { credentials: 'same-origin', signal });
      if (!res.ok) return;

      const html = await res.text();
      const meta = parseDocumentMeta(html);
      metaCache.set(key, { data: meta, time: Date.now() });
      evictMetaCache();

      if (requestId !== this.requestId) return;
      applyDocumentMeta(meta);
    } catch (err) {
      if (err.name === 'AbortError') return;
    }
  }

  // ── Prefetch ───────────────────────────────────────────────────────────────

  prefetch(url) {
    if (!canPrefetch()) return;
    if (this.activePrefetches >= PREFETCH_LIMIT) return;

    const key = createCacheKey(url);
    if (sectionCache.has(key) || this.prefetchQueue.has(key)) return;

    this.activePrefetches++;

    const promise = this.doPrefetch(url, this.prefetchAbortController.signal)
      .then((data) => {
        sectionCache.set(key, { data, time: Date.now() });
        evictCache();
      })
      .catch(() => {})
      .finally(() => {
        this.activePrefetches--;
        this.prefetchQueue.delete(key);
      });

    this.prefetchQueue.set(key, promise);
  }

  async doPrefetch(url, signal) {
    const ids = this.getSectionIds();
    if (!ids.length) throw new Error('Nothing to prefetch');

    const res = await fetch(buildSectionURL(url, ids), {
      credentials: 'same-origin',
      signal,
    });
    if (!res.ok) throw new Error('Prefetch failed');
    return res.json();
  }

  // ── Intersection Observer (prefetch on enter viewport) ──
  setupObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const link = entry.target;
          if (!(link instanceof HTMLAnchorElement)) continue;
          const url = this.getURL(link);
          if (url) this.prefetch(url);
          this.observer.unobserve(link);
        }
      },
      { rootMargin: '120px' }
    );
  }

  observeLinks() {
    if (!this.observer) return;
    this.observer.disconnect();
    for (const link of this.root.querySelectorAll(SELECTORS.link)) {
      if (link instanceof HTMLAnchorElement) this.observer.observe(link);
    }
  }

  scheduleInitialPrefetch() {
    if (!canPrefetch()) return;
    ric(() => {
      const links = [...this.root.querySelectorAll(SELECTORS.link)].slice(0, PREFETCH_LIMIT);
      for (const link of links) {
        if (!(link instanceof HTMLAnchorElement)) continue;
        const url = this.getURL(link);
        if (url) this.prefetch(url);
      }
    });
  }

  // ── Helpers ──

  getURL(link) {
    const href = link.getAttribute('href');
    if (!href) return null;
    const url = new URL(href, location.origin);
    return url.origin === location.origin ? url : null;
  }

  /** Update the active tile to reflect the given URL without touching the DOM outside the switcher. */
  syncCurrentState(url) {
    const handle = getCollectionHandle(url);

    for (const link of this.root.querySelectorAll(SELECTORS.link)) {
      if (!(link instanceof HTMLElement)) continue;
      const isCurrent = link.dataset.collectionHandle === handle;
      link.classList.toggle('collection-icon-scroller__link--current', isCurrent);
      if (isCurrent) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
  }
}

function initCollectionSwitchers(root = document) {
  root.querySelectorAll(SELECTORS.switcher).forEach((node) => {
    if (!(node instanceof HTMLElement) || instances.has(node)) return;
    const instance = new CollectionSwitcher(node);
    instance.init();
    instances.set(node, instance);
    instanceList.add(instance);
  });
}

function teardownCollectionSwitchers(root = document) {
  root.querySelectorAll(SELECTORS.switcher).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const instance = instances.get(node);
    if (!instance) return;
    instance.destroy();
    instances.delete(node);
    instanceList.delete(instance);
  });
}

document.addEventListener('shopify:section:load', (e) => {
  instanceList.forEach((i) => i.invalidateSectionCache());
  if (e.target instanceof HTMLElement) initCollectionSwitchers(e.target);
});

document.addEventListener('shopify:section:unload', (e) => {
  instanceList.forEach((i) => i.invalidateSectionCache());
  // If the switcher section itself was removed, destroy its instance so
  // event listeners are cleaned up and it is removed from tracking sets.
  if (e.target instanceof HTMLElement) teardownCollectionSwitchers(e.target);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCollectionSwitchers());
} else {
  initCollectionSwitchers();
}
